import React from 'react';
import { DetailCardType, HeaderParentItem } from 'types/DetailPageTypes';
import { TabType } from 'types/TabTypes';
import DetailPage from 'components/layout/DetailPage';
import { HeaderButtons } from 'types/ButtonTypes';
import { iconMapping } from 'services/utils/iconMapping';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLocationDot, faTrash } from '@fortawesome/free-solid-svg-icons';

const AssetDetail: React.FC = () => {
    const apiObject = 'asset';
    const pageObject = 'asset';
    const headerTitleDataField = 'name';

    // Show the icon of the object type in front of the name, the stack icon when the type has none
    const pageIcon = (data: any) => (
        <FontAwesomeIcon icon={iconMapping(data?.asset_type_icon || 'layer-group')} />
    );

    // Show the address and the parent objects in front of the name, each linking to its own page
    const headerParents = (data: any): HeaderParentItem[] => [
        ...(data?.location?.id ? [{
            key: `location-${data.location.id}`,
            label: data.location.address,
            url: `/contacts/work-locations/${data.location.id}`,
            icon: <FontAwesomeIcon icon={faLocationDot} />,
        }] : []),
        ...((data?.parent_assets ?? []).map((parentAsset: { id: number; name: string }) => ({
            key: `asset-${parentAsset.id}`,
            label: parentAsset.name,
            url: `/contacts/assets/${parentAsset.id}`,
        }))),
    ];

    const headerButtons: HeaderButtons = [
        {
            type: 'icon',
            icon: <FontAwesomeIcon icon={faTrash} />,
            tooltipText: 'general.delete',
            action: 'delete',
            onSuccess: 'redirect',
            redirectTo: 'contacts/assets',
            size: 'medium'
        },
    ];

    const tabs: TabType[] = [
        { label: 'asset.general.information_label', type: 'information' },
        { label: 'job.general.jobs_label', type: 'jobs' },
        { label: 'timeline.general.timeline_label', type: 'timeline', showOnlyTimelineUpdates: true },
        { label: 'document.general.documents_label', type: 'attachments' },
    ];

    const detailCards: DetailCardType[] = [
        {
            component: 'FieldsCard',
            title: 'asset.general.details_card_label',
            alignment: 'horizontal',
            fields: [
                {
                    name: 'name',
                    label: 'asset.general.name_label',
                    type: 'text',
                    placeholder: 'asset.general.name_placeholder',
                },
                { 
                    type: 'dropdown', 
                    name: 'asset_type', 
                    label: 'asset.general.asset_type_label', 
                    apiRequest: [{
                        endpoint: 'get_asset_type_list',
                        object: 'asset_type',
                        params: { deleted: false },
                        useCache: true
                    }],
                    disabled_selected: 'asset.general.type_disabled_selected',
                    allowNoneOption: false,
                },
                {
                    name: 'number',
                    label: 'asset.general.number_label',
                    type: 'text',
                    placeholder: 'asset.general.number_placeholder',
                },
                {
                    name: 'description',
                    label: 'asset.general.description_label',
                    type: 'textarea',
                    placeholder: 'asset.general.description_placeholder',
                },
                
            ]
        },
        {
            component: 'FieldsCard',
            title: 'asset.general.belongs_to_card',
            alignment: 'horizontal',
            fields: [
                { 
                    type: 'searchselect',
                    name: 'location',
                    label: 'asset.general.location_short_label',
                    placeholder: 'asset.general.location_placeholder',
                    objectName: 'location.general.object_name.singular',
                    query: { endpoint: 'get_location_search' },
                    viewModeLinkUrl: { baseUrl: '/contacts/work-locations' },
                    selectionFormat: 'address',
                    optionFormat: {
                        title: { field: 'address', format: (value) => value },
                    },
                },
                {
                    type: 'searchselect',
                    name: 'parent_asset',
                    label: 'asset.general.parent_asset_label',
                    placeholder: 'asset.general.parent_asset_placeholder',
                    objectName: 'asset.general.object_name.singular',
                    tooltipText: 'asset.general.parent_asset_tooltip',
                    query: { endpoint: 'get_asset_search' },
                    viewModeLinkUrl: { baseUrl: '/contacts/assets' },
                    selectionFormat: 'name',
                    optionFormat: {
                        title: { field: 'name', format: (value) => value },
                        subtitle: { field: 'number', format: (value) => value, optional: true },
                    },
                },
            ]
        },
        {
            component: 'QrCodeCard',
            title: 'asset.general.qr_code_label',
        },
    ];

    return (
        <DetailPage
            apiObject={apiObject}
            pageObject={pageObject}
            pageIcon={pageIcon}
            headerParents={headerParents}
            headerTitleDataField={headerTitleDataField}
            headerButtons={headerButtons}
            detailCards={detailCards}
            tabs={tabs}
        />
    );
};

export default AssetDetail;